/**
 * SHER-383 — when to prompt for a deletion reason on treatment smart fields.
 *
 * Outpatient ordonnance rows are removed locally until form submit; inpatient
 * rows with a submission_id call DELETE immediately. Both should skip the
 * justification modal during the same 1-hour grace window used for submission
 * edits (FillSurvey).
 */

import {
    isWithinSubmissionGracePeriod,
    SUBMISSION_GRACE_PERIOD_MS,
} from '../../../utils/submissionGracePeriod';
import { shouldDeleteTreatmentRowViaApi } from './treatmentRowPersistence';

export { SUBMISSION_GRACE_PERIOD_MS as TREATMENT_DELETION_GRACE_PERIOD_MS };

export const GRACE_PERIOD_DELETION_REASON = 'Deleted within grace period';

export interface TreatmentDeletionContext {
    fieldType: string;
    submissionId: unknown;
    rowCreatedAt?: string | Date | null;
    submissionCreatedAt?: string | Date | null;
    now?: number;
}

export function buildTreatmentDeletionContext(
    fieldType: string,
    treatment: Record<string, unknown> | null | undefined,
    submissionCreatedAt?: string | Date | null,
    now: number = Date.now(),
): TreatmentDeletionContext {
    return {
        fieldType,
        submissionId: treatment?.submission_id,
        rowCreatedAt: treatment?.createdAt as string | Date | null | undefined,
        submissionCreatedAt,
        now,
    };
}

export function shouldRequireDeletionReason(context: TreatmentDeletionContext): boolean {
    const { fieldType, submissionId, rowCreatedAt, submissionCreatedAt, now } = context;

    if (!shouldDeleteTreatmentRowViaApi(fieldType, submissionId)) {
        return false;
    }

    const referenceCreatedAt = rowCreatedAt ?? submissionCreatedAt;
    return !isWithinSubmissionGracePeriod(referenceCreatedAt, now);
}

export function resolveDeletionReason(
    userReason: string,
    context: TreatmentDeletionContext,
): string {
    if (shouldRequireDeletionReason(context)) {
        return userReason.trim();
    }

    return userReason.trim() || GRACE_PERIOD_DELETION_REASON;
}
